import Vue from "vue";
import AlbumService from "@/services/Album";

const STALE_DATA_TIME = 3600000;
const state = {
  featuredReleases: {
    albumIds: [],
    fetchedAt: 0,
    error: false,
    fetching: false,
    perPage: 0,
    total: 0
  },
  albums: {},
  libraryAlbums: {
    albumIds: [],
    query: "",
    perPage: 0,
    total: 0,
    fetching: false,
    fetchedAt: 0,
    error: false
  },
  albumTracks: {},
  albumStats: {},
  totalStreams: {
    count: null,
    fetchedAt: 0,
    fetching: false,
    error: false
  }
};

const getters = {
  getAlbumById: state => id => state.albums[id],

  featuredReleaseStale({ featuredReleases }) {
    return Date.now() - featuredReleases.fetchedAt > STALE_DATA_TIME;
  },
  featuredReleaseAlbums({ featuredReleases, albums }) {
    return featuredReleases.albumIds.map(id => albums[id]);
  },
  libraryAlbums({ libraryAlbums, albums }) {
    return libraryAlbums.albumIds.map(id => albums[id]);
  },
  libraryAlbumsQuery({ libraryAlbums }) {
    return libraryAlbums.query;
  },
  libraryAlbumsCount({ libraryAlbums }) {
    return libraryAlbums.total;
  },
  fetchingLibraryAlbums({ libraryAlbums }) {
    return libraryAlbums.fetching;
  },
  libraryAlbumsStale({ libraryAlbums }) {
    return Date.now() - libraryAlbums.fetchedAt > STALE_DATA_TIME;
  },
  libraryAlbumsTotalPages({ libraryAlbums }) {
    return Math.ceil(libraryAlbums.total / libraryAlbums.perPage) || 0;
  },
  libraryAlbum: ({ albums }) => albumId => albums[albumId] || {},
  albumTracks: ({ albumTracks }) => albumId => albumTracks[albumId]?.data || [],
  albumStats: ({ albumStats }) => albumId => albumStats[albumId]?.data || {},
  fetchingAlbumData: ({ albumStats }) => albumId =>
    !!albumStats[albumId]?.fetching,
  fetchingAlbumStats: ({ albumStats }) => albumId =>
    !!albumStats[albumId]?.fetching,
  fetchingAlbumTracks: ({ albumTracks }) => albumId =>
    !!albumTracks[albumId]?.fetching,
  totalStreams: ({ totalStreams }) => totalStreams.count,
  totalStreamsStale: ({ totalStreams }) => {
    return Date.now() - totalStreams.fetchedAt > STALE_DATA_TIME;
  }
};

const mutations = {
  ADD_LIBRARY_ALBUMS({ albums, libraryAlbums }, data) {
    if (data.results?.length) {
      data.results.forEach(album => {
        Vue.set(albums, album.id, {
          ...album,
          fetchedAt: data.fetchedAt
        });
      });
      const albumIds = data.results.map(album => album.id);
      if (
        data.pagination.perPage !== libraryAlbums.perPage ||
        data.pagination.total !== libraryAlbums.total
      ) {
        libraryAlbums.albumIds = new Array(albumIds.length).fill("");
        libraryAlbums.albumIds.splice(
          (data.pagination.page - 1) * libraryAlbums.perPage,
          data.results.length,
          ...albumIds
        );
        libraryAlbums.perPage = data.pagination.perPage;
        libraryAlbums.total = data.pagination.total;
        libraryAlbums.fetchedAt = data.fetchedAt;
      } else {
        libraryAlbums.albumIds.splice(
          (data.pagination.page - 1) * libraryAlbums.perPage,
          data.results.length,
          ...albumIds
        );
      }
    }
  },
  SET_FEATURED_RELEASES({ albums, featuredReleases }, data) {
    if (data.results?.length) {
      data.results.forEach(album => {
        Vue.set(albums, album.id, {
          ...album,
          fetchedAt: data.fetchedAt
        });
      });
      const albumIds = data.results.map(album => album.id);
      if (
        data.pagination.perPage !== featuredReleases.perPage ||
        data.pagination.total !== featuredReleases.total
      ) {
        featuredReleases.albumIds = new Array(albumIds.length).fill("");
        featuredReleases.albumIds.splice(
          (data.pagination.page - 1) * featuredReleases.perPage,
          data.results.length,
          ...albumIds
        );
        featuredReleases.perPage = data.pagination.perPage;
        featuredReleases.total = data.pagination.total;
        featuredReleases.fetchedAt = data.fetchedAt;
      } else {
        featuredReleases.albumIds.splice(
          (data.pagination.page - 1) * featuredReleases.perPage,
          data.results.length,
          ...albumIds
        );
      }
    }
  },
  SET_LIBRARY_ALBUMS_FETCHING({ libraryAlbums }, value) {
    libraryAlbums.fetching = value;
  },
  SET_LIBRARY_ALBUMS_ERROR({ libraryAlbums }, hasError) {
    libraryAlbums.error = hasError;
  },
  SET_LIBRARY_ALBUMS_QUERY({ libraryAlbums }, query) {
    libraryAlbums.query = query;
  },
  RESET_LIBRARY_ALBUMS(state) {
    Vue.set(state, "libraryAlbums", {
      albumIds: [],
      query: "",
      perPage: 0,
      total: 0,
      fetching: false,
      fetchedAt: 0,
      error: false
    });
  },
  ADD_ALBUM_INFO({ albums, libraryAlbums }, album) {
    const { songIds } = albums[album.id] || {};
    Vue.set(albums, album.id, {
      ...album,
      title: album.albumTitle,
      coverArtUrl: album.coverArt,
      created: album.createdOn,
      releaseStatus: album.status,
      status: albums[album.id]?.status,
      ...(!!songIds && { songIds })
    });
  },
  SET_ALBUM_TRACKS_FETCHING({ albumTracks }, { albumId, value }) {
    const entry = albumTracks[albumId] || {};
    Vue.set(albumTracks, albumId, {
      ...entry,
      fetching: value
    });
  },
  SET_ALBUM_TRACKS_ERROR({ albumTracks }, { albumId, value }) {
    const entry = albumTracks[albumId] || {};
    Vue.set(albumTracks, albumId, {
      ...entry,
      fetching: false,
      fetchedAt: 0,
      error: value
    });
  },
  SET_ALBUM_TRACKS({ albumTracks }, { albumId, tracks }) {
    const entry = albumTracks[albumId] || {};
    Vue.set(albumTracks, albumId, {
      ...entry,
      data: tracks,
      fetchedAt: Date.now(),
      error: false
    });
  },
  SET_ALBUM_STATS_FETCHING({ albumStats }, { albumId, value }) {
    const entry = albumStats[albumId] || {};
    Vue.set(albumStats, albumId, {
      ...entry,
      fetching: value
    });
  },
  SET_ALBUM_STATS_ERROR({ albumStats }, { albumId, value }) {
    const entry = albumStats[albumId] || {};
    Vue.set(albumStats, albumId, {
      ...entry,
      fetching: false,
      fetchedAt: 0,
      error: value
    });
  },
  SET_ALBUM_STATS({ albumStats }, { albumId, startDate, endDate, data }) {
    const entry = albumStats[albumId] || {};
    Vue.set(albumStats, albumId, {
      ...entry,
      startDate,
      endDate,
      data,
      fetchedAt: Date.now(),
      error: false
    });
  },
  SET_FEATURED_RELEASES_ERROR({ featuredReleases }, hasError) {
    featuredReleases.error = hasError;
  },
  SET_FEATURED_RELEASES_FETCHING({ featuredReleases }, value) {
    featuredReleases.fetching = value;
  },
  SET_TOTAL_STREAMS_FETCHING({ totalStreams }, value) {
    totalStreams.fetching = value;
  },
  SET_TOTAL_STREAMS_ERROR({ totalStreams }, hasError) {
    totalStreams.error = hasError;
  },
  SET_TOTAL_STREAMS({ totalStreams }, { count, fetchedAt }) {
    totalStreams.count = count;
    totalStreams.fetchedAt = fetchedAt;
  },
  REMOVE_LIBRARY_ALBUM(state, albumId) {
    const index = state.libraryAlbums.albumIds.indexOf(albumId);
    if (index !== -1) {
      state.libraryAlbums.albumIds.splice(index, 1);
      state.libraryAlbums.total -= 1;
      state.libraryAlbums.fetchedAt = 0;
    }
  }
};

const actions = {
  async fetchLibraryAlbums({ commit }, { shouldReset, ...params }) {
    commit("SET_LIBRARY_ALBUMS_FETCHING", true);
    const res = await AlbumService.getUserAlbums(params);
    commit("SET_LIBRARY_ALBUMS_FETCHING", false);
    if (res.status === 200 && res.data) {
      if (res.data.results.length === 0 && shouldReset) {
        commit("RESET_LIBRARY_ALBUMS");
      } else {
        commit("ADD_LIBRARY_ALBUMS", {
          ...res.data,
          fetchedAt: Date.now()
        });
      }
      commit("SET_LIBRARY_ALBUMS_ERROR", false);
    } else {
      commit("SET_LIBRARY_ALBUMS_ERROR", true);
    }
  },
  async fetchAlbumInfo({ commit }, albumId) {
    const res = await AlbumService.getAlbumInfo(albumId);
    if (res.status === 200 && res.data) {
      commit("ADD_ALBUM_INFO", res.data);
    }
  },
  async fetchFeaturedRelease({ commit }) {
    commit("SET_FEATURED_RELEASES_FETCHING", true);
    const res = await AlbumService.getReleases();
    commit("SET_FEATURED_RELEASES_FETCHING", false);
    if (res.status === 200 && res.data) {
      commit("SET_FEATURED_RELEASES", {
        ...res.data,
        fetchedAt: Date.now()
      });
      commit("SET_FEATURED_RELEASES_ERROR", false);
    } else {
      commit("SET_FEATURED_RELEASES_ERROR", true);
    }
  },
  async fetchTotalStreams({ commit }) {
    commit("SET_TOTAL_STREAMS_FETCHING", true);
    const res = await AlbumService.getUserStreams();
    commit("SET_TOTAL_STREAMS_FETCHING", false);
    if (res.status === 200 && res.data) {
      commit("SET_TOTAL_STREAMS", {
        count: res.data,
        fetchedAt: Date.now()
      });
      commit("SET_TOTAL_STREAMS_ERROR", false);
    } else {
      commit("SET_TOTAL_STREAMS_ERROR", true);
    }
  },
  async fetchAlbumTracks({ commit }, albumId) {
    commit("SET_ALBUM_TRACKS_FETCHING", { albumId, value: true });
    const res = await AlbumService.getAlbumTracks(albumId);
    commit("SET_ALBUM_TRACKS_FETCHING", { albumId, value: false });
    if (res.status === 200 && res.data) {
      commit("SET_ALBUM_TRACKS", { albumId, tracks: res.data });
    }
  },
  async fetchAlbumStats({ commit }, { albumId, startDate, endDate }) {
    commit("SET_ALBUM_STATS_FETCHING", { albumId, value: true });
    const res = await AlbumService.getAlbumStreams(albumId, startDate, endDate);
    commit("SET_ALBUM_STATS_FETCHING", { albumId, value: false });
    if (res.status === 200 && res.data) {
      commit("SET_ALBUM_STATS", {
        albumId,
        startDate,
        endDate,
        data: res.data
      });
    }
  },
  resetLibrary({ commit }) {
    commit("RESET_LIBRARY");
  },
  setLibraryAlbumsQuery({ commit }, query) {
    commit("SET_LIBRARY_ALBUMS_QUERY", query);
  },
  removeLibraryAlbum({ commit }, albumId) {
    commit("REMOVE_LIBRARY_ALBUM", albumId);
  }
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
